if (!customElements.get('ja-logo-marquee')) {
  customElements.define('ja-logo-marquee', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.manual = this.querySelector('[data-logo-manual]');
      this.auto = this.querySelector('.ja-logo-marquee__auto');
      this.viewport = this.querySelector('.ja-logo-marquee__viewport');
      this.track = this.querySelector('.ja-logo-marquee__track');
      this.toggle = this.querySelector('.ja-logo-marquee__toggle');
      this.items = Array.from(this.manual.querySelector('.ja-grid').children);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.desktop = matchMedia('(min-width: 990px)');
      this.speed = Math.max(10, Math.min(60, Number(this.dataset.speed) || 20));
      this.userPaused = false;
      this.hovered = false;
      this.visible = false;
      this.editorSelected = false;
      this.imagesReady = false;
      this.layoutKey = null;
      this.buildTrack();

      this.toggle.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updatePlayback();
      }, { signal });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.updatePlayback();
      }, { signal });
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.updatePlayback();
      }, { signal });
      this.viewport.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        this.userPaused = !this.userPaused;
        this.updatePlayback();
      }, { signal });
      this.track.addEventListener('focusin', (event) => this.focusLogo(event), { signal });
      this.copy.addEventListener('mousedown', (event) => {
        if (event.target.closest('a')) event.preventDefault();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.motion.addEventListener('change', () => this.updateLayout(), { signal });
      this.desktop.addEventListener('change', () => this.updateLayout(), { signal });
      this.section = this.closest('.shopify-section') || this;
      ['shopify:section:select', 'shopify:block:select'].forEach((type) => {
        this.section.addEventListener(type, () => {
          this.editorSelected = true;
          this.updateLayout();
        }, { signal });
      });
      ['shopify:section:deselect', 'shopify:block:deselect'].forEach((type) => {
        this.section.addEventListener(type, () => {
          this.editorSelected = false;
          this.updateLayout();
        }, { signal });
      });
      this.resizeObserver = new ResizeObserver(() => this.updateLayout());
      this.resizeObserver.observe(this);
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.updatePlayback();
      });
      this.intersectionObserver.observe(this);
      Promise.all(Array.from(this.track.querySelectorAll('img'), (image) => image.decode().catch(() => {})))
        .then(() => {
          if (signal.aborted) return;
          this.imagesReady = true;
          this.updateLayout();
        });
    }

    buildTrack() {
      this.group = document.createElement('ul');
      this.group.className = 'ja-logo-marquee__group';
      this.group.setAttribute('role', 'list');
      this.items.forEach((item, index) => {
        const clone = item.cloneNode(true);
        clone.className = 'ja-logo-marquee__item';
        clone.dataset.logoIndex = index;
        [clone, ...clone.querySelectorAll('*')].forEach((element) => {
          element.removeAttribute('id');
          element.removeAttribute('data-shopify-editor-block');
        });
        clone.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
        this.group.append(clone);
      });
      this.copy = this.group.cloneNode(true);
      this.copy.setAttribute('aria-hidden', 'true');
      this.copy.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      this.track.replaceChildren(this.group, this.copy);
    }

    updateLayout() {
      const columns = Number(this.desktop.matches ? this.dataset.desktopColumns : this.dataset.mobileColumns) || 1;
      const styles = getComputedStyle(this);
      const gapProperty = this.desktop.matches ? '--grid-desktop-horizontal-spacing' : '--grid-mobile-horizontal-spacing';
      const gap = parseFloat(styles.getPropertyValue(gapProperty)) || 0;
      const width = this.clientWidth;
      const baseWidth = Math.max(1, Math.floor((width - gap * (columns - 1)) / columns));
      const mobileWidth = parseFloat(styles.getPropertyValue('--ja-logo-mobile-width')) || 112;
      const mobileHeight = parseFloat(styles.getPropertyValue('--ja-logo-mobile-height')) || 40;
      const widths = this.items.map((item, index) => {
        const percentage = Math.max(50, Math.min(100, Number(item.dataset.logoSlotWidth) || 100));
        if (this.desktop.matches) return Math.max(1, Math.floor(baseWidth * percentage / 100));
        const image = this.group.children[index].querySelector('img');
        const imageWidth = image?.naturalHeight ? image.naturalWidth / image.naturalHeight * mobileHeight : mobileWidth;
        const slotWidth = Math.max(mobileHeight, Math.floor(Math.min(mobileWidth, imageWidth) * percentage / 100));
        item.style.setProperty('--ja-mobile-item-width', `${slotWidth}px`);
        return slotWidth;
      });
      const contentWidth = widths.reduce((total, itemWidth) => total + itemWidth, 0) + gap * (widths.length - 1);
      if (!this.imagesReady || this.motion.matches || this.editorSelected || contentWidth <= width || width === 0 || styles.direction === 'rtl') {
        this.animation?.cancel();
        this.animation = null;
        this.layoutKey = null;
        this.auto.hidden = true;
        const wasHidden = this.manual.hidden;
        this.manual.hidden = false;
        if (wasHidden) this.manual.querySelector('slider-component')?.resetPages();
        return;
      }
      this.manual.hidden = true;
      this.auto.hidden = false;
      const key = `${width}:${columns}:${gap}:${widths.join(',')}`;
      if (this.layoutKey === key) return;
      const phase = this.animation && this.duration ? (Number(this.animation.currentTime) % this.duration) / this.duration : 0;
      this.animation?.cancel();
      this.style.setProperty('--ja-marquee-gap', `${gap}px`);
      // Whole-pixel slots keep repeated logos on the same pixel grid at the loop boundary.
      [this.group, this.copy].forEach((group) => {
        Array.from(group.children).forEach((item, index) => {
          item.style.setProperty('--ja-marquee-item-width', `${widths[index]}px`);
        });
      });
      this.distance = this.group.getBoundingClientRect().width;
      this.duration = this.distance / this.speed * 1000;
      this.layoutKey = key;
      this.viewport.scrollLeft = 0;
      this.animation = this.track.animate([
        { transform: 'translateX(0)' },
        { transform: `translateX(-${this.distance}px)` }
      ], { duration: this.duration, iterations: Infinity, easing: 'linear' });
      this.animation.currentTime = phase * this.duration;
      this.updatePlayback();
    }

    focusLogo(event) {
      const item = event.target.closest('[data-logo-index]');
      if (!item || !this.animation) return;
      this.userPaused = true;
      this.updatePlayback();
      const original = this.group.children[Number(item.dataset.logoIndex)];
      const left = original.offsetLeft - this.group.offsetLeft;
      const position = Math.max(0, Math.min(left - (this.viewport.clientWidth - original.clientWidth) / 2, this.distance - this.viewport.clientWidth));
      this.animation.currentTime = position / this.speed * 1000;
      this.viewport.scrollLeft = 0;
      if (this.copy.contains(event.target)) original.querySelector('a')?.focus({ preventScroll: true });
    }

    updatePlayback() {
      if (!this.animation) return;
      if (this.userPaused || this.hovered || !this.visible || document.hidden) this.animation.pause();
      else this.animation.play();
      const label = this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
      this.toggle.setAttribute('aria-label', label);
      this.toggle.title = label;
      this.toggle.querySelector('[data-logo-play]').hidden = !this.userPaused;
      this.toggle.querySelector('[data-logo-pause]').hidden = this.userPaused;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.animation?.cancel();
      this.animation = null;
    }
  });
}
